import React, { useEffect, useState } from 'react'
import { Button, ConfigProvider, Dropdown, Menu, message, Pagination, Table, Tag, Switch, Modal, Card, Row, Col, Tooltip, Popover, Checkbox, Space } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { Wrapper } from '../style';
import moment from 'moment';
import { ConvertParams, defaultPageSize } from '../../../../config';
import { MakeApiCall } from '../../../../apis';
import { useThemeMode } from "../../../../contexts/ThemeContext";
import { getLocalAntdTheme } from "../../../../theme/antdTheme";

const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowSize;
};

/** Parse nested JSON strings (e.g. receive_on from list API) into an array. */
// function parseReceiveOnForClone(raw) {
//     let t = raw;
//     try {
//         while (typeof t === 'string') {
//             t = JSON.parse(t);
//         }
//         return Array.isArray(t) ? t : [];
//     } catch {
//         return [];
//     }
// }

// function parseAsinListForClone(raw) {
//     if (raw == null || raw === '') return [];
//     if (Array.isArray(raw)) return raw.map(String);
//     try {
//         const parsed = JSON.parse(raw);
//         return Array.isArray(parsed) ? parsed.map(String) : [];
//     } catch {
//         return [];
//     }
// }

// function normalizeEmailsForClone(v) {
//     if (Array.isArray(v)) return [...v];
//     if (typeof v === 'string') return v.split(',').map((e) => e.trim()).filter(Boolean);
//     return [];
// }

// function normalizeMobilesForClone(row) {
//     if (Array.isArray(row.send_on_mobile_numbers)) return [...row.send_on_mobile_numbers];
//     if (typeof row.send_on_mobile_numbers === 'string') {
//         return row.send_on_mobile_numbers.split(',').map((w) => w.trim()).filter(Boolean);
//     }
//     if (row.send_on_mobile_number) return [row.send_on_mobile_number];
//     return [];
// }

/** POST add-alert body from an existing row: new name; no id/uuid/timestamps. status 1 = inactive (same as list Switch). */
// function buildAlertClonePayload(row) {
//     const baseName = String(row.name || '').trim() || 'Alert';
//     const payload = {
//         name: `${baseName}-copy`,
//         category: row.category,
//         type: row.type,
//         frequency: row.frequency,
//         receive_on: parseReceiveOnForClone(row.receive_on),
//         asin_list: parseAsinListForClone(row.asin_list),
//         severity: row.severity || 'Medium',
//         send_on_emails: normalizeEmailsForClone(row.send_on_emails),
//         send_on_mobile_numbers: normalizeMobilesForClone(row),
//         status: 1,
//     };
//     if (row.user_input !== undefined && row.user_input !== null && row.user_input !== '') {
//         payload.user_input = row.user_input;
//     }
//     return payload;
// }

/** Parse nested JSON strings (e.g. receive_on from list API) into an array. */
function parseReceiveOnForClone(raw) {
    let t = raw;
    try {
        while (typeof t === 'string') {
            t = JSON.parse(t);
        }
        return Array.isArray(t) ? t : [];
    } catch {
        return [];
    }
}

function parseAsinListForClone(raw) {
    if (raw == null || raw === '') return [];
    if (Array.isArray(raw)) return raw.map(String);
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
        return [];
    }
}

function normalizeEmailsForClone(v) {
    if (Array.isArray(v)) return [...v];
    if (typeof v === 'string') return v.split(',').map((e) => e.trim()).filter(Boolean);
    return [];
}

function normalizeMobilesForClone(row) {
    if (Array.isArray(row.send_on_mobile_numbers)) return [...row.send_on_mobile_numbers];
    if (typeof row.send_on_mobile_numbers === 'string') {
        return row.send_on_mobile_numbers.split(',').map((w) => w.trim()).filter(Boolean);
    }
    if (row.send_on_mobile_number) return [row.send_on_mobile_number];
    return [];
}

/** POST add-alert body from an existing row: new name; no id/uuid/timestamps. status 1 = inactive (same as list Switch). */
function buildAlertClonePayload(row) {
    const baseName = String(row.name || '').trim() || 'Alert';
    const payload = {
        name: `${baseName}-copy`,
        category: row.category,
        type: row.type,
        frequency: row.frequency,
        receive_on: parseReceiveOnForClone(row.receive_on),
        asin_list: parseAsinListForClone(row.asin_list),
        severity: row.severity || 'Medium',
        send_on_emails: normalizeEmailsForClone(row.send_on_emails),
        send_on_mobile_numbers: normalizeMobilesForClone(row),
        status: 1,
    };
    if (row.user_input !== undefined && row.user_input !== null && row.user_input !== '') {
        payload.user_input = row.user_input;
    }
    return payload;
}

function Settings({
    userData,
    setIsModalOpen,
    setEditing,
    exposeFetcher,
    searchText,
    selectedCategory,
    selectedSeverity,
    selectedReceiveOn,
    selectedAlertType,
    selectedAsins,
}) {
    const { themeMode } = useThemeMode();

    const [totalPage, setTotalPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);
    const [updatingIds, setUpdatingIds] = useState(new Set());

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [cloningId, setCloningId] = useState(null);

    const [filters, setFilters] = useState({
        page: 1,
        'per-page': defaultPageSize,
        name: searchText || '',
        category: undefined,
        severity: undefined,
        receive_on: undefined,
        alert_type: undefined,
        asin_list: undefined,
    });

    const windowSize = useWindowSize();
    const isMobile = windowSize.width < 768;
    const isTablet = windowSize.width >= 768 && windowSize.width < 1024;

    const fetchAlertSettingList = async () => {
        setLoading(true);
        try {
            const response = await MakeApiCall(
                `get-alerts${ConvertParams(filters)}`,
                'get',
                null,
                true,
                {},
                userData?.token
            );

            if (response?.status) {
                const records = response?.data?.records || [];
                const pagination = response?.data?.pagination || {};
                setList(records);
                setTotalPage(pagination.totalCount || 0);
                setSelectedRowKeys([]);
            } else {
                message.warning(response?.message || "Failed to load alert settings");
            }
        } catch (error) {
            message.error('Error fetching alert settings.');
            console.error('API Error:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userData?.token) {
            fetchAlertSettingList();
        }
    }, [filters, userData?.token]);

    useEffect(() => {
        if (exposeFetcher) {
            exposeFetcher(() => fetchAlertSettingList);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exposeFetcher]);

    // Sync parent filter props into local filters
    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            page: 1,
            name: searchText || '',
            category: selectedCategory || undefined,
            severity: selectedSeverity || undefined,
            receive_on: selectedReceiveOn || undefined,
            alert_type: selectedAlertType || undefined,
            asin_list: selectedAsins?.length ? selectedAsins.join(',') : undefined,
        }));
    }, [searchText, selectedCategory, selectedSeverity, selectedReceiveOn, selectedAlertType, selectedAsins]);

    const handlePaginationChange = (currentPage, newPageSize) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            page: currentPage,
            'per-page': newPageSize,
        }));
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        return moment.unix(timestamp).format("YYYY-MM-DD HH:mm:ss");
    };

    const bannerHeight = userData?.user?.user_data?.trial_expires_at ? 40 : 0;

    const handleBulkAction = async (action) => {
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one alert');
            return;
        }

        const actionLabels = {
            delete: 'delete',
            enable: 'enable',
            disable: 'disable'
        };

        const confirmMessages = {
            delete: {
                title: 'Bulk Delete Alerts',
                content: `Are you sure you want to delete ${selectedRowKeys.length} alert(s)? This action cannot be undone.`,
                okText: 'Delete',
                okType: 'danger'
            },
            enable: {
                title: 'Bulk Enable Alerts',
                content: `Are you sure you want to enable ${selectedRowKeys.length} alert(s)?`,
                okText: 'Enable',
                okType: 'default'
            },
            disable: {
                title: 'Bulk Disable Alerts',
                content: `Are you sure you want to disable ${selectedRowKeys.length} alert(s)?`,
                okText: 'Disable',
                okType: 'default'
            }
        };

        Modal.confirm({
            ...confirmMessages[action],
            cancelText: 'Cancel',
            onOk: async () => {
                setBulkActionLoading(true);
                try {
                    const response = await MakeApiCall(
                        'alerts-bulk',
                        'post',
                        {
                            action: actionLabels[action],
                            selected_ids: selectedRowKeys
                        },
                        true,
                        {},
                        userData?.token
                    );

                    if (response?.status) {
                        message.success(`Successfully ${actionLabels[action]}d ${selectedRowKeys.length} alert(s)`);
                        fetchAlertSettingList();
                        setSelectedRowKeys([]);
                    } else {
                        message.error(response?.message || `Failed to ${action} alerts`);
                    }
                } catch (error) {
                    console.error('Bulk action error:', error);
                    message.error(`Error performing bulk ${action}`);
                } finally {
                    setBulkActionLoading(false);
                }
            }
        });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedRowKeys(list.map(item => item.id));
        } else {
            setSelectedRowKeys([]);
        }
    };

    const handleSelectRow = (id, checked) => {
        if (checked) {
            setSelectedRowKeys([...selectedRowKeys, id]);
        } else {
            setSelectedRowKeys(selectedRowKeys.filter(key => key !== id));
        }
    };

    const bulkActionMenu = (
        <Menu className="min-w-200px p-3">
            <Menu.Item
                key="enable"
                className="d-flex align-items-center mb-2"
                onClick={() => handleBulkAction('enable')}
                disabled={selectedRowKeys.length === 0}
            >
                <i className="ki-outline ki-check-circle fs-3 me-2 text-success"></i>
                Enable Selected
                {selectedRowKeys.length > 0 && <span className="ms-2 badge">( {selectedRowKeys.length} )</span>}
            </Menu.Item>
            <Menu.Item
                key="disable"
                className="d-flex align-items-center mb-2"
                onClick={() => handleBulkAction('disable')}
                disabled={selectedRowKeys.length === 0}
            >
                <i className="ki-outline ki-cross-circle fs-3 me-2 text-warning"></i>
                Disable Selected
                {selectedRowKeys.length > 0 && <span className="ms-2 badge">( {selectedRowKeys.length} )</span>}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
                key="delete"
                className="d-flex align-items-center"
                onClick={() => handleBulkAction('delete')}
                disabled={selectedRowKeys.length === 0}
            >
                <i className="ki-outline ki-trash fs-3 me-2 text-danger"></i>
                Delete Selected
                {selectedRowKeys.length > 0 && <span className="ms-2 badge">( {selectedRowKeys.length} )</span>}
            </Menu.Item>
        </Menu>
    );

    const renderDetailModal = () => {
        if (!selectedRecord) return null;

        return (
            <Modal
                title="Alert Details"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setModalVisible(false)}>Close</Button>
                ]}
                width={isMobile ? '95%' : 600}
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
                <div className="alert-detail-modal">
                    <div className="detail-section">
                        <div className="detail-row">
                            <span className="detail-label">ID:</span>
                            <span className="detail-value">{selectedRecord.id}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Name:</span>
                            <span className="detail-value fw-bold">{selectedRecord.name}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Category:</span>
                            <span className="detail-value">{selectedRecord.category || '-'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Type:</span>
                            <span className="detail-value">{selectedRecord.type || '-'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Severity:</span>
                            <span className="detail-value">
                                {selectedRecord.severity ? (
                                    <Tag color={selectedRecord.severity === 'Major' ? 'volcano' : 'green'}>
                                        {selectedRecord.severity}
                                    </Tag>
                                ) : '-'}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Frequency:</span>
                            <span className="detail-value">{selectedRecord.frequency || '-'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Sent On:</span>
                            <span className="detail-value">
                                {(() => {
                                    let items = [];
                                    let rawText = selectedRecord.receive_on;
                                    try {
                                        while (typeof rawText === 'string') rawText = JSON.parse(rawText);
                                        if (Array.isArray(rawText)) items = rawText.sort((a, b) => a.localeCompare(b));
                                    } catch (e) {}
                                    return items.map((item) => {
                                        let color = 'blue';
                                        if (item.toLowerCase() === 'whatsapp') color = 'green';
                                        else if (item.toLowerCase() === 'email') color = 'volcano';
                                        else if (item.toLowerCase() === 'all alerts') color = 'purple';
                                        return <Tag key={item} color={color}>{item}</Tag>;
                                    });
                                })()}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">ASIN List:</span>
                            <span className="detail-value">
                                {(() => {
                                    let items = [];
                                    try {
                                        if (selectedRecord.asin_list) items = JSON.parse(selectedRecord.asin_list);
                                    } catch (e) {
                                        return <Tag>N/A</Tag>;
                                    }
                                    return items.map((item) => <Tag key={item}>{item}</Tag>);
                                })()}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Updated At:</span>
                            <span className="detail-value">{formatTimestamp(selectedRecord.updated_at)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Status:</span>
                            <span className="detail-value">
                                <Tag color={selectedRecord.status === 0 ? 'green' : 'red'}>
                                    {selectedRecord.status === 0 ? 'Active' : 'Inactive'}
                                </Tag>
                            </span>
                        </div>
                    </div>
                </div>
            </Modal>
        );
    };

    const renderCardView = () => {
        if (loading) return <div className="text-center py-5">Loading...</div>;
        if (list.length === 0) return <div className="text-center py-5">No alerts found</div>;

        const allSelected = selectedRowKeys.length === list.length && list.length > 0;
        const someSelected = selectedRowKeys.length > 0 && selectedRowKeys.length < list.length;

        return (
            <>
                <div className="mb-3">
                    <Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleSelectAll}>
                        Select All ({list.length})
                    </Checkbox>
                </div>

                <div className="alert-cards-container">
                    {list.map((record) => (
                        <Card
                            key={record.id}
                            className="mb-3"
                            style={{
                                borderRadius: '8px',
                                borderLeft: `2px solid ${record.severity === 'Major' ? '#ff4d4f' : record.severity === 'Minor' ? '#52c41a' : '#d9d9d9'}`,
                                backgroundColor: record.status === 1 ? '#f5f5f5' : '#fff'
                            }}
                        >
                            <div className="card-content">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div className="d-flex align-items-center">
                                        <Checkbox
                                            checked={selectedRowKeys.includes(record.id)}
                                            onChange={(e) => handleSelectRow(record.id, e.target.checked)}
                                            className="me-2"
                                        />
                                        <div className="fw-bolder text-dark fs-6 text-one">{record.name}</div>
                                    </div>
                                    <div>
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => { setSelectedRecord(record); setModalVisible(true); }}
                                            icon={<i className="ki-solid ki-eye fs-3"></i>}
                                            className="me-1"
                                        />
                                        <Dropdown overlay={menu(record)} trigger={['click']}>
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<i className="ki-solid ki-dots-vertical fs-3"></i>}
                                            />
                                        </Dropdown>
                                    </div>
                                </div>

                                <Row gutter={[8, 8]} className="mb-2">
                                    <Col span={12}>
                                        <div className="fs-7">
                                            <div className="text-muted">Category:</div>
                                            <div className="ms-0 fw-semibold">{record.category || '-'}</div>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="fs-7">
                                            <div className="text-muted">Type:</div>
                                            <div className="ms-0 fw-semibold">{record.type || '-'}</div>
                                        </div>
                                    </Col>
                                </Row>

                                <Row gutter={[8, 8]} className="mb-4 mt-4">
                                    <Col span={12}>
                                        <div className="fs-7">
                                            <span className="text-muted">Severity:</span>
                                            <span className="ms-2">
                                                {record.severity ? (
                                                    <Tag color={record.severity === 'Major' ? 'volcano' : 'green'}>{record.severity}</Tag>
                                                ) : '-'}
                                            </span>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="fs-7">
                                            <span className="text-muted">Status:</span>
                                            <span className="ms-2">
                                                <Tag color={record.status === 0 ? 'green' : 'default'}>
                                                    {record.status === 0 ? 'Active' : 'Inactive'}
                                                </Tag>
                                            </span>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="mb-2">
                                    <div className="fs-7">
                                        <span className="text-muted">Frequency:</span>
                                        <span className="ms-2">{record.frequency || '-'}</span>
                                    </div>
                                </div>

                                <Button
                                    type="link"
                                    className="p-0 mt-2"
                                    onClick={() => { setSelectedRecord(record); setModalVisible(true); }}
                                >
                                    View all details <i className="ki-outline ki-arrow-right fs-6 ms-1"></i>
                                </Button>
                            </div>
                        </Card>
                    ))}
                </div>
            </>
        );
    };

    const page = filters.page || 1;
    const pageSize = filters['per-page'] || defaultPageSize;

    const columns = [
        {
            title: () => (
                <Checkbox
                    checked={selectedRowKeys.length === list.length && list.length > 0}
                    indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < list.length}
                    onChange={handleSelectAll}
                />
            ),
            key: "selection",
            render: (_, record) => (
                <Checkbox
                    checked={selectedRowKeys.includes(record.id)}
                    onChange={(e) => handleSelectRow(record.id, e.target.checked)}
                />
            ),
        },
        {
            title: "#",
            key: "index",
            render: (_, __, index) => (page - 1) * pageSize + index + 1
        },
        { title: "Name", dataIndex: "name", key: "name", render: (text) => <div className='fw-bold'>{text}</div> },
        { title: "Category", dataIndex: "category", key: "category", className: "min-w-125px" },
        { title: "Type", dataIndex: "type", key: "type" },
        { title: "Severity", dataIndex: "severity", key: "severity", width: 100 },
        { title: "Frequency", dataIndex: "frequency", key: "frequency", width: 120 },
        {
            title: "Alert Channel",
            dataIndex: "receive_on",
            key: "receive_on",
            render: (text) => {
                let items = [];
                let rawText = text;
                try {
                    while (typeof rawText === 'string') rawText = JSON.parse(rawText);
                    if (Array.isArray(rawText)) items = rawText.sort((a, b) => a.localeCompare(b));
                } catch (e) {}

                return items.map((item) => {
                    let color = 'blue';
                    if (item.toLowerCase() === 'whatsapp') color = 'green';
                    else if (item.toLowerCase() === 'email') color = 'volcano';
                    else if (item.toLowerCase() === 'all alerts') color = 'purple';
                    return <Tag key={item} color={color}>{item}</Tag>;
                });
            }
        },
        {
            title: "ASIN List",
            dataIndex: "asin_list",
            key: "asin_list",
            render: (text) => {
                let items = [];
                try {
                    if (text) items = JSON.parse(text);
                } catch (e) {
                    return <Tag>N/A</Tag>;
                }
                if (!Array.isArray(items) || items.length === 0) return <Tag>N/A</Tag>;

                if (items.length <= 2) return items.map((item) => <Tag key={item}>{item}</Tag>);

                const visibleAsins = items.slice(0, 2);
                const hiddenAsins = items.slice(2);

                return (
                    <>
                        {visibleAsins.map((item) => <Tag key={item}>{item}</Tag>)}
                        <Popover
                            content={<div>{hiddenAsins.map((item) => <Tag key={item}>{item}</Tag>)}</div>}
                            title="More ASINs"
                        >
                            <Tag color="blue" style={{ cursor: 'pointer' }}>+{hiddenAsins.length}</Tag>
                        </Popover>
                    </>
                );
            },
        },
        {
            title: "Updated At",
            dataIndex: "updated_at",
            key: "updated_at",
            render: (timestamp) => formatTimestamp(timestamp),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (_, row) => {
                const isActive = row?.status === 0;
                const updating = updatingIds.has(row.id);
                return (
                    <div className="d-flex align-items-center gap-2">
                        <Switch checked={isActive} loading={updating} onChange={() => handleToggleStatus(row)} />
                        <span className="fs-8">{isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                );
            },
        },
        {
            title: "Action",
            key: "action",
            render: (_, row) => (
                <Dropdown overlay={menu(row)} trigger={["click"]}>
                    <Button type="link" data-kt-menu-trigger="click" data-kt-menu-placement="bottom-end" data-kt-menu-overflow="true">
                        <i className="ki-solid ki-dots-vertical fs-1 text-gray-800"></i>
                    </Button>
                </Dropdown>
            ),
        },
    ];

    const menu = (row) => (
        <Menu className="min-w-100px p-3">
            <Menu.Item key="edit" className="d-flex align-items-center mb-2" onClick={() => handleEdit(row)}>
                <i className="ki-outline ki-pencil fs-3 me-2"></i>Edit
            </Menu.Item>
            <Menu.Item
                key="clone"
                className="d-flex align-items-center mb-2"
                disabled={cloningId === row.id}
                onClick={() => handleClone(row)}
            >
                <i className="ki-outline ki-copy fs-3 me-2"></i>
                Clone
            </Menu.Item>
            <Menu.Item key="delete" className="d-flex align-items-center mb-2" onClick={() => handleDelete(row)}>
                <i className="ki-outline ki-trash fs-3 me-2"></i>Delete
            </Menu.Item>
        </Menu>
    );

    const handleEdit = (value) => {
        setEditing(value);
        setIsModalOpen(true);
    };

    const handleClone1= async (row) => {
        if (!row?.id) return;
        setCloningId(row.id);
        try {
            const payload = buildAlertClonePayload(row);
            const resp = await MakeApiCall('add-alert', 'post', payload, true, {}, userData?.token);
            if (resp?.status) {
                message.success('Alert cloned successfully');
                fetchAlertSettingList();
            } else {
                message.error(resp?.message || 'Failed to clone alert');
            }
        } catch (err) {
            console.error('Error cloning alert:', err);
            message.error('Error cloning alert');
        } finally {
            setCloningId(null);
        }
    };

    const handleClone = async (row) => {
        if (!row?.id) return;
        setCloningId(row.id);
        try {
            const payload = buildAlertClonePayload(row);
            const resp = await MakeApiCall('add-alert', 'post', payload, true, {}, userData?.token);
            if (resp?.status) {
                message.success('Alert cloned successfully');
                fetchAlertSettingList();
            } else {
                message.error(resp?.message || 'Failed to clone alert');
            }
        } catch (err) {
            console.error('Error cloning alert:', err);
            message.error('Error cloning alert');
        } finally {
            setCloningId(null);
        }
    };

    const handleToggleStatus = async (row) => {
        if (!row || !row.id) return;
        const newStatus = row.status === 1 ? 0 : 1;
        setUpdatingIds(prev => new Set(prev).add(row.id));
        try {
            const resp = await MakeApiCall(`update-alert/${row.id}`, 'post', { status: newStatus }, true, {}, userData?.token);
            if (resp?.status) {
                setList(prev => prev.map(item => item.id === row.id ? { ...item, status: newStatus } : item));
                message.success('Alert status updated');
            } else {
                message.error(resp?.message || 'Failed to update status');
            }
        } catch (err) {
            message.error('Error updating alert status');
        } finally {
            setUpdatingIds(prev => { const s = new Set(prev); s.delete(row.id); return s; });
        }
    };

    const handleDelete = (row) => {
        if (!row || !row.id) return;
        Modal.confirm({
            title: 'Delete Alert',
            content: 'Are you sure you want to delete this alert? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                setUpdatingIds(prev => new Set(prev).add(row.id));
                try {
                    const resp = await MakeApiCall(`delete-alert/${row.id}`, 'delete', null, true, {}, userData?.token);
                    if (resp?.status) {
                        setList(prev => prev.filter(item => item.id !== row.id));
                        message.success('Alert deleted');
                    } else {
                        message.error(resp?.message || 'Failed to delete alert');
                    }
                } catch (err) {
                    message.error('Error deleting alert');
                } finally {
                    setUpdatingIds(prev => { const s = new Set(prev); s.delete(row.id); return s; });
                }
            }
        });
    };

    const theme = getLocalAntdTheme(themeMode, "#f45b68");

    const mobileStyles = `
        .alert-detail-modal .detail-section { margin-top: 20px; }
        .alert-detail-modal .detail-row { display: flex; padding: 12px 0; border-bottom: 1px solid #f0f0f0; }
        .alert-detail-modal .detail-row:last-child { border-bottom: none; }
        .alert-detail-modal .detail-label { width: 100px; font-weight: 500; color: #8c8c8c; flex-shrink: 0; }
        .alert-detail-modal .detail-value { flex: 1; color: #262626; word-break: break-word; }
        .alert-cards-container .text-one {
            overflow: hidden; text-overflow: ellipsis;
            display: -webkit-box; -webkit-line-clamp: 2;
            -webkit-box-orient: vertical; white-space: normal;
        }
        @media (max-width: 768px) { .ant-card-body { padding: 12px; } }
        .bulk-action-bar { position: sticky; top: 0; z-index: 100; background-color: white; border: 1px solid #e8e8e8; }
    `;

    return (
        <Wrapper>
            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    <div className="card card-xxl-stretch mb-5 mb-xl-8">
                        <div className="card-header border-bottom-dashed">
                            <h3 className="card-title fw-bolder">Alerts List</h3>
                            <div className="card-toolbar">
                                {selectedRowKeys.length > 0 && (
                                    <Space>
                                        <span className="me-2 text-muted">{selectedRowKeys.length} selected</span>
                                        <Dropdown overlay={bulkActionMenu} trigger={['click']}>
                                            <Button
                                                type="primary"
                                                danger
                                                loading={bulkActionLoading}
                                                icon={<i className="ki-outline ki-down fs-3 text-light"></i>}
                                            >
                                                Bulk Actions ({selectedRowKeys.length})
                                            </Button>
                                        </Dropdown>
                                        <Button onClick={() => setSelectedRowKeys([])}>Clear</Button>
                                    </Space>
                                )}
                            </div>
                        </div>
                        <div className="card-body py-3 px-3">
                            <div>
                                {isMobile ? renderCardView() : (
                                    <Table
                                        scroll={{ x: "max-content" }}
                                        sticky={{ offsetHeader: bannerHeight }}
                                        columns={columns}
                                        loading={loading}
                                        dataSource={list}
                                        rowKey="id"
                                        pagination={false}
                                        components={{
                                            table: (props) => (
                                                <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...props} />
                                            ),
                                            header: {
                                                wrapper: (props) => <thead {...props} className={props.className || ''} />,
                                                row: (props) => <tr {...props} className={`${props.className || ''} fw-bolder text-dark bg-cus`} />,
                                                cell: (props) => <th {...props} className={props.className || ''} />,
                                            },
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    <div className="card card-xxl-stretch mb-5 mb-xl-8">
                        <div className="card-body py-2">
                            <ConfigProvider theme={theme}>
                                <Pagination
                                    className="mt-3"
                                    responsive
                                    current={filters.page}
                                    pageSize={filters["per-page"]}
                                    total={totalPage}
                                    showSizeChanger
                                    onChange={handlePaginationChange}
                                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                                    size={isMobile ? 'small' : 'default'}
                                    simple={isMobile}
                                />
                            </ConfigProvider>
                        </div>
                    </div>
                </div>
            </div>

            {renderDetailModal()}
            <style>{mobileStyles}</style>
        </Wrapper>
    );
}

export default Settings;